import * as yup from 'yup';

import { AcquisitionType } from '../types/types';

const acquisitionTitleFields = yup.object({
  title: yup.string().required('O título é obrigatório'),
  subtitle: yup.string().required('O subtítulo é obrigatório'),
});

export const offerBlockSchema = yup.object({
  basePaymentOfferId: yup.string().required('Selecione uma oferta da loja'),
  promotionalOffer: yup.boolean().optional(),
  tempPaymentOfferId: yup.string().when('promotionalOffer', {
    is: true,
    then: schema => schema.required('Selecione a oferta temporária'),
    otherwise: schema => schema.optional(),
  }),
  tag: yup.string().optional(),
  payOffer: yup.mixed().optional(),
});

export const purchaseSchema = acquisitionTitleFields.concat(offerBlockSchema);

export const multiOfferSchema = acquisitionTitleFields.concat(
  yup.object({
    offers: yup.array().of(offerBlockSchema).max(5).required(),
  })
);

export const audienceSchema = yup.object({
  enabled: yup.boolean().required(),
  acquisitionTypes: yup
    .array()
    .of(yup.string<AcquisitionType>().required())
    .when('enabled', {
      is: true,
      then: schema => schema.min(1, 'Selecione pelo menos 1 tipo de aquisição'),
    })
    .required(),
  purchase: yup
    .mixed<yup.InferType<typeof purchaseSchema>>()
    .when(['enabled', 'acquisitionTypes'], {
      is: (enabled: boolean, types: AcquisitionType[]) =>
        enabled && types?.includes(AcquisitionType.Purchase),
      then: () => purchaseSchema,
      otherwise: () => yup.mixed().optional(),
    }),
  subscription: yup
    .mixed<yup.InferType<typeof multiOfferSchema>>()
    .when(['enabled', 'acquisitionTypes'], {
      is: (enabled: boolean, types: AcquisitionType[]) =>
        enabled && types?.includes(AcquisitionType.Subscription),
      then: () => multiOfferSchema,
      otherwise: () => yup.mixed().optional(),
    }),
  rental: yup
    .mixed<yup.InferType<typeof multiOfferSchema>>()
    .when(['enabled', 'acquisitionTypes'], {
      is: (enabled: boolean, types: AcquisitionType[]) =>
        enabled && types?.includes(AcquisitionType.Rental),
      then: () => multiOfferSchema,
      otherwise: () => yup.mixed().optional(),
    }),
});

export const configurationSchema = yup
  .object({
    subscriber: audienceSchema,
    freemium: audienceSchema,
  })
  .test(
    'at-least-one-enabled-audience',
    'Selecione pelo menos um público para configurar as ofertas',
    value => Boolean(value?.subscriber?.enabled || value?.freemium?.enabled)
  );

export type OfferBlockData = yup.InferType<typeof offerBlockSchema>;
export type PurchaseData = yup.InferType<typeof purchaseSchema>;
export type MultiOfferData = yup.InferType<typeof multiOfferSchema>;
export type AudienceFormData = yup.InferType<typeof audienceSchema>;
export type ConfigurationFormData = yup.InferType<typeof configurationSchema>;

export type AcquisitionTypeData = OfferBlockData;

export const emptyOfferBlock: OfferBlockData = {
  basePaymentOfferId: '',
  promotionalOffer: false,
  tempPaymentOfferId: '',
  tag: '',
  payOffer: undefined,
};

export const emptyPurchase: PurchaseData = {
  title: '',
  subtitle: '',
  ...emptyOfferBlock,
};

export const emptyMultiOffer: MultiOfferData = {
  title: '',
  subtitle: '',
  offers: [emptyOfferBlock],
};

export const configurationDefaultValues: ConfigurationFormData = {
  subscriber: {
    enabled: true,
    acquisitionTypes: [AcquisitionType.Purchase],
    purchase: emptyPurchase,
    subscription: emptyMultiOffer,
    rental: emptyMultiOffer,
  },
  freemium: {
    enabled: false,
    acquisitionTypes: [AcquisitionType.Purchase],
    purchase: emptyPurchase,
    subscription: emptyMultiOffer,
    rental: emptyMultiOffer,
  },
};
